import React from 'react';
import { FormikProps } from 'formik';
import { FormGroup } from '../../../../components/molecules/form/FormGroup';
import { Input } from '../../../../components/atoms/Input';
import { InputPhone } from '../../../../components/atoms/InputPhone';
import { PaymentMethodType } from '../../../../models/subscription';
import { useSubscriptionPlans } from '../../../../hooks/api/subscription/useSubscriptionPlans';
import {
  BillingPeriodSelector,
  PlanSelectionCards,
  PriceSummary,
} from '../../../../components/organisms/subscription';
import { useTranslation } from '@hooks/useTranslation';

// SVG Credit Card Icon to replace MUI
const CreditCardIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
    />
  </svg>
);

export interface PaymentMethodFormData {
  paymentMethodType: PaymentMethodType | '';
  selectedPlanId?: string;
  billingFirstName: string;
  billingLastName: string;
  billingEmail: string;
  billingAddress1: string;
  billingAddress2?: string;
  billingCity: string;
  billingState: string;
  billingZip: string;
  billingCountry: string;
  billingPhone?: string;
  ccNumber?: string;
  ccExp?: string;
  cvv?: string;
  checkName?: string;
  checkAba?: string;
  checkAccount?: string;
  accountType?: string;
  accountHolderType?: string;
}

export interface BillingUserData {
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface PaymentMethodSelectorProps {
  formik: FormikProps<any>;
  userData?: BillingUserData;
  showBillingCycle?: boolean;
  defaultBillingCycle?: 'monthly' | 'annual';
  /** Show billing period selection (Monthly/Annual). Default: true */
  showBillingPeriod?: boolean;
  /** Show plan selection cards. Default: true */
  showPlanSelection?: boolean;
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  formik,
  userData,
  showBillingCycle = false, // Deprecated, keeping for backwards compatibility
  defaultBillingCycle = 'monthly',
  showBillingPeriod = true,
  showPlanSelection = true,
}) => {
  // Sprint 28, story-143. Bucket 1 (pre-auth) — see docs/architecture.md "## Sprint 28" Decision 4.
  const tLogin = useTranslation('login');
  // Fetch subscription plans (NEW SYSTEM)
  const { data: subscriptionPlans, isLoading: isLoadingPlans } =
    useSubscriptionPlans();

  // Filter public plans (not addons)
  const publicPlans = React.useMemo(() => {
    if (!subscriptionPlans) return [];
    return subscriptionPlans.filter((plan) => plan.isPublic && !plan.isAddOn);
  }, [subscriptionPlans]);

  // Get selected plan or default to first public plan
  const selectedPlan = React.useMemo(() => {
    if (!publicPlans.length) return null;
    if (formik.values.selectedPlanId) {
      return (
        publicPlans.find((p) => p.id === formik.values.selectedPlanId) ||
        publicPlans[0]
      );
    }
    return publicPlans[0];
  }, [publicPlans, formik.values.selectedPlanId]);


  // Track if billing info has been auto-populated
  const hasAutoPopulatedRef = React.useRef(false);

  // Auto-populate billing information from user data if provided and fields are empty
  // Only run once when component mounts and userData is available
  React.useEffect(() => {
    if (hasAutoPopulatedRef.current) return;

    if (
      userData &&
      !formik.values.billingFirstName &&
      !formik.values.billingLastName &&
      !formik.values.billingEmail
    ) {
      hasAutoPopulatedRef.current = true;

      const updates: Record<string, string> = {};

      if (userData.firstName) {
        updates.billingFirstName = userData.firstName;
      }
      if (userData.lastName) {
        updates.billingLastName = userData.lastName;
      }
      if (userData.email) {
        updates.billingEmail = userData.email;
      }

      // Only update if we have values to set
      if (Object.keys(updates).length > 0) {
        formik.setValues(
          {
            ...formik.values,
            ...updates,
          },
          false,
        ); // false = don't validate immediately
      }
    }
    // Keyed on the user data itself, not just mount: `userData` is fed from values the
    // user types in an earlier registration step, so an empty mount pass must not be the
    // only chance to fill these. hasAutoPopulatedRef still guarantees it runs once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.firstName, userData?.lastName, userData?.email]);

  // Track if initial values have been set
  const hasInitializedRef = React.useRef(false);

  // Initialize billing period and selected plan if not set
  React.useEffect(() => {
    // Only initialize once when publicPlans are loaded
    if (hasInitializedRef.current || publicPlans.length === 0) return;

    let needsUpdate = false;

    if (!formik.values.billingPeriod) {
      formik.setFieldValue(
        'billingPeriod',
        defaultBillingCycle.toUpperCase(),
        false,
      );
      needsUpdate = true;
    }
    // Auto-select first public plan if none selected
    if (!formik.values.selectedPlanId && publicPlans.length > 0) {
      formik.setFieldValue('selectedPlanId', publicPlans[0].id, false);
      needsUpdate = true;
    }

    if (needsUpdate || publicPlans.length > 0) {
      hasInitializedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicPlans.length]);

  // Calculate pricing based on billing period and selected plan
  const isAnnual = formik.values.billingPeriod === 'ANNUAL';
  const monthlyCost = selectedPlan ? selectedPlan.basePriceCents / 100 : 0;
  const displayPrice = isAnnual ? monthlyCost * 12 : monthlyCost;

  return (
    <div className="space-y-6">
      {/* Billing Period Selection */}
      {showBillingPeriod && (
        <BillingPeriodSelector
          selectedPeriod={formik.values.billingPeriod as 'MONTHLY' | 'ANNUAL'}
          onPeriodChange={(period) => formik.setFieldValue('billingPeriod', period)}
          monthlyCost={monthlyCost}
          showPricing={true}
        />
      )}

      {/* Payment Method - Credit Card Only */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          {tLogin.paymentMethodLabel}
        </label>
        <div className="flex items-center p-4 border-2 border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
          <CreditCardIcon className="h-8 w-8 text-indigo-600 mr-3" />
          <div>
            <p className="font-medium text-gray-900 dark:text-white">
              {tLogin.creditOrDebitCard}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {tLogin.cardBrandsHint}
            </p>
          </div>
        </div>
      </div>

      {/* Subscription Plan Selection */}
      {showPlanSelection && !isLoadingPlans && publicPlans.length > 0 && (
        <>
          <PlanSelectionCards
            plans={publicPlans}
            selectedPlanId={formik.values.selectedPlanId || publicPlans[0]?.id || null}
            onSelectPlan={(planId) => formik.setFieldValue('selectedPlanId', planId)}
            billingPeriod={formik.values.billingPeriod as 'MONTHLY' | 'ANNUAL'}
            isLoading={isLoadingPlans}
          />

          {/* Summary card for selected plan */}
          {selectedPlan && (
            <PriceSummary
              amount={displayPrice}
              billingPeriod={formik.values.billingPeriod as 'MONTHLY' | 'ANNUAL'}
              planName={selectedPlan.name}
            />
          )}
        </>
      )}

      {/* Billing Information */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          {tLogin.billingInformation}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormGroup label={tLogin.firstNameLabel} htmlFor="billingFirstName">
            <Input
              id="billingFirstName"
              name="billingFirstName"
              type="text"
              value={formik.values.billingFirstName || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(
                formik.touched.billingFirstName &&
                  formik.errors.billingFirstName,
              )}
            />
            {formik.touched.billingFirstName &&
              formik.errors.billingFirstName && (
                <p className="text-sm text-red-500 mt-1">
                  {String(formik.errors.billingFirstName)}
                </p>
              )}
          </FormGroup>

          <FormGroup label={tLogin.lastNameLabel} htmlFor="billingLastName">
            <Input
              id="billingLastName"
              name="billingLastName"
              type="text"
              value={formik.values.billingLastName || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(
                formik.touched.billingLastName && formik.errors.billingLastName,
              )}
            />
            {formik.touched.billingLastName &&
              formik.errors.billingLastName && (
                <p className="text-sm text-red-500 mt-1">
                  {String(formik.errors.billingLastName)}
                </p>
              )}
          </FormGroup>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <FormGroup label={tLogin.email} htmlFor="billingEmail">
            <Input
              id="billingEmail"
              name="billingEmail"
              type="email"
              value={formik.values.billingEmail || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(
                formik.touched.billingEmail && formik.errors.billingEmail,
              )}
            />
            {formik.touched.billingEmail && formik.errors.billingEmail && (
              <p className="text-sm text-red-500 mt-1">
                {String(formik.errors.billingEmail)}
              </p>
            )}
          </FormGroup>

          <FormGroup label={tLogin.phone} htmlFor="billingPhone">
            <InputPhone
              name="billingPhone"
              value={formik.values.billingPhone || ''}
              country="us"
              placeholder="+1 (415) 555-0123"
              setFieldValue={formik.setFieldValue}
              error={Boolean(
                formik.touched.billingPhone && formik.errors.billingPhone,
              )}
              isdisabled={false}
            />
            {formik.touched.billingPhone && formik.errors.billingPhone && (
              <p className="text-sm text-red-500 mt-1">
                {String(formik.errors.billingPhone)}
              </p>
            )}
          </FormGroup>
        </div>

        <div className="grid grid-cols-1 gap-4 mt-4">
          <FormGroup label={tLogin.address} htmlFor="billingAddress1">
            <Input
              id="billingAddress1"
              name="billingAddress1"
              type="text"
              value={formik.values.billingAddress1 || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(
                formik.touched.billingAddress1 && formik.errors.billingAddress1,
              )}
            />
            {formik.touched.billingAddress1 &&
              formik.errors.billingAddress1 && (
                <p className="text-sm text-red-500 mt-1">
                  {String(formik.errors.billingAddress1)}
                </p>
              )}
          </FormGroup>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <FormGroup label={tLogin.city} htmlFor="billingCity">
            <Input
              id="billingCity"
              name="billingCity"
              type="text"
              value={formik.values.billingCity || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(
                formik.touched.billingCity && formik.errors.billingCity,
              )}
            />
            {formik.touched.billingCity && formik.errors.billingCity && (
              <p className="text-sm text-red-500 mt-1">
                {String(formik.errors.billingCity)}
              </p>
            )}
          </FormGroup>

          <FormGroup label={tLogin.state} htmlFor="billingState">
            <Input
              id="billingState"
              name="billingState"
              type="text"
              placeholder="CA"
              maxLength={2}
              value={formik.values.billingState || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(
                formik.touched.billingState && formik.errors.billingState,
              )}
            />
            {formik.touched.billingState && formik.errors.billingState && (
              <p className="text-sm text-red-500 mt-1">
                {String(formik.errors.billingState)}
              </p>
            )}
          </FormGroup>

          <FormGroup label={tLogin.zipCode} htmlFor="billingZip">
            <Input
              id="billingZip"
              name="billingZip"
              type="text"
              value={formik.values.billingZip || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(
                formik.touched.billingZip && formik.errors.billingZip,
              )}
            />
            {formik.touched.billingZip && formik.errors.billingZip && (
              <p className="text-sm text-red-500 mt-1">
                {String(formik.errors.billingZip)}
              </p>
            )}
          </FormGroup>
        </div>
      </div>

      {/* Payment Details */}
      {formik.values.paymentMethodType === PaymentMethodType.CREDIT_CARD && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            {tLogin.cardInformation}
          </h3>

          <div className="grid grid-cols-1 gap-4">
            <FormGroup label={tLogin.cardNumber} htmlFor="ccNumber">
              <Input
                id="ccNumber"
                name="ccNumber"
                type="text"
                placeholder="4111 1111 1111 1111"
                maxLength={19}
                value={formik.values.ccNumber || ''}
                onChange={(e) => {
                  // Format card number with spaces
                  const value = e.target.value.replace(/\s/g, '');
                  const formattedValue =
                    value.match(/.{1,4}/g)?.join(' ') || value;
                  formik.setFieldValue('ccNumber', formattedValue);
                }}
                onBlur={formik.handleBlur}
                error={Boolean(
                  formik.touched.ccNumber && formik.errors.ccNumber,
                )}
              />
              {formik.touched.ccNumber && formik.errors.ccNumber && (
                <p className="text-sm text-red-500 mt-1">
                  {String(formik.errors.ccNumber)}
                </p>
              )}
            </FormGroup>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <FormGroup label={tLogin.expirationDate} htmlFor="ccExp">
              <Input
                id="ccExp"
                name="ccExp"
                type="text"
                placeholder="12/25"
                maxLength={5}
                value={formik.values.ccExp || ''}
                onChange={(e) => {
                  // Format expiration date
                  let value = e.target.value.replace(/\D/g, '');
                  if (value.length >= 2) {
                    value = value.slice(0, 2) + '/' + value.slice(2, 4);
                  }
                  formik.setFieldValue('ccExp', value);
                }}
                onBlur={formik.handleBlur}
                error={Boolean(formik.touched.ccExp && formik.errors.ccExp)}
              />
              {formik.touched.ccExp && formik.errors.ccExp && (
                <p className="text-sm text-red-500 mt-1">
                  {String(formik.errors.ccExp)}
                </p>
              )}
            </FormGroup>

            <FormGroup label={tLogin.cvv} htmlFor="cvv">
              <Input
                id="cvv"
                name="cvv"
                type="text"
                placeholder="123"
                maxLength={4}
                value={formik.values.cvv || ''}
                onChange={(e) => {
                  // Only allow digits
                  const value = e.target.value.replace(/\D/g, '');
                  formik.setFieldValue('cvv', value);
                }}
                onBlur={formik.handleBlur}
                error={Boolean(formik.touched.cvv && formik.errors.cvv)}
              />
              {formik.touched.cvv && formik.errors.cvv && (
                <p className="text-sm text-red-500 mt-1">
                  {String(formik.errors.cvv)}
                </p>
              )}
            </FormGroup>
          </div>

          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 mt-4">
            <div className="flex items-center">
              <svg
                className="h-5 w-5 text-green-400 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-xs text-green-700 dark:text-green-300">
                {tLogin.paymentSecureHint}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethodSelector;
